import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Phone, MessageSquare, AlertTriangle } from 'lucide-react';

export default function Dashboard() {
  const [stats, setStats] = useState<any>(null);
  const [recentLookups, setRecentLookups] = useState<any[]>([]);
  const [recentMessages, setRecentMessages] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.stats(), api.lookups({ limit: 5 }), api.messages({ limit: 5 })])
      .then(([s, l, m]) => {
        setStats(s);
        setRecentLookups(l.results || []);
        setRecentMessages(m.results || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="text-muted-foreground">Loading dashboard…</div>;
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-muted-foreground mt-1">
          Carrier lookups and email-to-SMS activity overview
        </p>
      </div>

      {/* Notice */}
      <Card className="border-amber-500/50 bg-amber-500/5">
        <CardContent className="pt-6 flex gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium">Gateway status (2026)</p>
            <p className="text-muted-foreground mt-1">
              Most major carrier email-to-SMS gateways are shut down or degraded. Verizon (
              <code className="text-xs">@vtext.com</code>) remains partially functional until March
              31, 2027. Google Fi and some smaller carriers still work. Always verify delivery.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Lookups today</CardDescription>
            <CardTitle className="text-3xl">{stats?.lookupsToday ?? 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Messages sent today</CardDescription>
            <CardTitle className="text-3xl">{stats?.messagesToday ?? 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Verizon detections</CardDescription>
            <CardTitle className="text-3xl text-red-500">
              {stats?.verizonDetections ?? 0}
            </CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Quick actions */}
      <div className="flex flex-wrap gap-3">
        <Button asChild>
          <Link to="/lookup">
            <Phone className="h-4 w-4 mr-2" />
            New Lookup
          </Link>
        </Button>
        <Button variant="outline" asChild>
          <Link to="/composer">
            <MessageSquare className="h-4 w-4 mr-2" />
            Compose SMS
          </Link>
        </Button>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Recent lookups */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Recent Lookups</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {recentLookups.length === 0 && (
              <p className="text-sm text-muted-foreground">No lookups yet.</p>
            )}
            {recentLookups.map((l) => (
              <div
                key={l.id}
                className="flex items-center justify-between text-sm border-b border-border/50 pb-2 last:border-0"
              >
                <div>
                  <p className="font-mono">{l.national_format || l.phone_number}</p>
                  <p className="text-muted-foreground text-xs">{l.carrier_name || 'Unknown'}</p>
                </div>
                <div className="flex items-center gap-2">
                  {l.is_verizon ? (
                    <Badge variant="verizon">Verizon</Badge>
                  ) : (
                    <Badge variant="secondary">{l.line_type || '—'}</Badge>
                  )}
                </div>
              </div>
            ))}
            <Button variant="link" asChild className="px-0">
              <Link to="/history">View all →</Link>
            </Button>
          </CardContent>
        </Card>

        {/* Recent messages */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Recent Messages</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {recentMessages.length === 0 && (
              <p className="text-sm text-muted-foreground">No messages sent yet.</p>
            )}
            {recentMessages.map((m) => (
              <div
                key={m.id}
                className="flex items-center justify-between text-sm border-b border-border/50 pb-2 last:border-0"
              >
                <div className="min-w-0">
                  <p className="font-mono truncate">{m.gateway_email}</p>
                  <p className="text-muted-foreground text-xs truncate max-w-[200px]">
                    {m.body?.slice(0, 40)}
                    {m.body?.length > 40 ? '…' : ''}
                  </p>
                </div>
                <Badge
                  variant={
                    m.status === 'sent' ? 'success' : m.status === 'failed' ? 'destructive' : 'secondary'
                  }
                >
                  {m.status}
                </Badge>
              </div>
            ))}
            <Button variant="link" asChild className="px-0">
              <Link to="/history">View all →</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
